import { Suspense } from "react"
import { useMutation } from "@blitzjs/rpc"
import { useSession } from "@blitzjs/auth"
import { BlitzPage } from "@blitzjs/next"
import { useRouter } from "next/router"
import Loading from "app/core/components/Loading"
import Form from "app/core/components/Form"
import { Button } from "app/core/components/Button"
import { FORM_ERROR } from "final-form"
import { HiArrowRight } from "react-icons/hi"
import { BookingLayout } from "app/core/layouts/BookingLayout"
import { useErrorHandler } from "react-error-boundary"
import { StartNewBookingInput } from "app/bookings/validations"
import { z } from "zod"
import { Tracking } from "app/core/tracking"
import { InitialUserDetails } from "pages/book-testing"
import Spinner from "react-md-spinner"
import style from "app/bookings/bookings.module.css"
import startNewBooking from "app/bookings/mutations/startNewBooking"

function StarSignup() {
  const session = useSession({ suspense: false })
  const router = useRouter()
  const [startNewBookingMutation] = useMutation(startNewBooking)
  const handleError = useErrorHandler()

  const schema = session.userId
    ? StartNewBookingInput
    : StartNewBookingInput.extend({
        email: z.string().email(),
      })

  return (
    <div className="bg-gray-100">
      <div className="pt-12 sm:pt-16 lg:pt-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">
              Renaissance® Star™
            </h2>
          </div>
        </div>
      </div>
      <div className="mt-8 sm:mt-12 ">
        <div className="relative">
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:grid grid-cols-1 ">
              <div className="py-2 px-3 text-center bg-gray-50 lg:flex lg:flex-col lg:justify-center lg:p-4 h-full col-span-4">
                <Form
                  schema={schema}
                  initialValues={{
                    bookingType: "star",
                    agreeToTestInUS: true,
                  }}
                  onSubmit={async (values) => {
                    try {
                      if (values.email) {
                        Tracking.setEmail(values.email)
                      }
                      await startNewBookingMutation(values)
                      if (values.email) router.push(`/star/new/1?guest=${values.email}`)
                      else router.push(`/star/new/1`)
                    } catch (error) {
                      if (error.name === "AuthenticationError") {
                        handleError(error)
                      }
                      return {
                        [FORM_ERROR]:
                          "Sorry, we had an unexpected error. Please try again. - " +
                          error.toString(),
                      }
                    }
                  }}
                  className="w-full mx-auto"
                >
                  {(form) => (
                    <div className={style["bookings-container"]}>
                      <Suspense fallback={<Loading />}>
                        <InitialUserDetails />
                      </Suspense>

                      <div className="col-span-3 bg-white px-6 py-8 lg:p-12 text-left">
                        <h3 className="text-2xl leading-8 font-extrabold text-gray-900 sm:text-3xl sm:leading-9">
                          Renaissance® Star™ standardized tests for Grades K-12 are 100% online,
                          nationally-normed, adaptive, and achievement-based.
                        </h3>

                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Setting up your account for these self-administered assessments is quick
                          and easy, and you can test as soon as the following day, then anytime you
                          want after that throughout your six-month subscription period.
                        </p>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Star tests are available to any student, anywhere in the world, and your
                          subscription includes unlimited access to all three subject options - Star
                          Early Literacy, Star Reading, and Star Math - as well as the comprehensive
                          Renaissance Next™ platform, which provides:
                        </p>

                        <ul
                          className="mt-4 text-base leading-6 text-gray-500 px-4"
                          style={{ listStyleType: "circle" }}
                        >
                          <li>A record book with interactive reports</li>
                          <li>Lesson planning assistance</li>
                          <li>Intervention resources</li>
                          <li>Various educational apps to support instruction</li>
                        </ul>

                        <p className="mt-4 text-base leading-6 text-primary-500">
                          <a
                            href="http://homeschoolboss.com/renaissance-star"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Read more about Renaissance® Star™
                          </a>
                        </p>

                        <h4 className="text-lg mt-4 font-semibold text-gray-600">
                          System Requirements
                        </h4>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Before registering, please make sure your device meets the Renaissance
                          system’s
                          <a
                            className="text-primary-500"
                            href="https://renaissance.widen.net/view/pdf/h4gqu07t5y/R43121.pdf?t.download=true&u=zceria"
                            target="_blank"
                            rel="nooppener noreferrer"
                          >
                            {" "}
                            Technical Requirements
                          </a>{" "}
                          for Star tests.
                        </p>

                        <h4 className="text-lg mt-4 font-semibold text-gray-600">
                          Renaissance Star Pricing
                        </h4>
                        <p className="mt-4 text-base leading-6 text-gray-500">
                          Includes six months of unlimited access to Star tests and the Renaissance
                          Next™ platform:
                        </p>

                        <h5 className="text-md mt-4 font-semibold text-gray-600">
                          Families and small groups (1-14 Students)
                        </h5>
                        <p className="text-base leading-6 text-gray-500">
                          $75 per student, up to a maximum of $450
                        </p>

                        <h5 className="text-md mt-4 font-semibold text-gray-600">
                          Large groups and schools (15+ Students)
                        </h5>
                        <p className="mb-4 text-base leading-6 text-gray-500">$30 per student*</p>

                        <b className="block text-sm text-base leading-6 mb-2 bg-brand-lightTurquoise p-4">
                          Attention Florida Testers:
                        </b>
                        <i className="block text-sm text-base leading-6 mb-2">
                          Our STAR tests are approved for{" "}
                          <a
                            href="https://www.stepupforstudents.org/"
                            target="_blank"
                            className="text-sm text-base leading-6 text-primary-500"
                            rel="nooppener noreferrer"
                          >
                            Step Up for Students
                          </a>{" "}
                          recipients for at-home, parent-administered testing.
                        </i>
                        <i className="block text-sm text-base leading-6 mb-2">
                          Families who wish to use STAR who are <b>not</b> Step Up for Students
                          recipients <b>may</b> need to arrange for a Florida-certified teacher to
                          administer the STAR tests that will be used for score report submission.
                          Please verify if this is a requirement for you - many families can{" "}
                          <a
                            href="https://www.fldoe.org/schools/school-choice/home-edu/district-home-edu-contacts.stml"
                            className="text-sm text-base leading-6 text-primary-500"
                          >
                            access this service for free
                          </a>{" "}
                          by contacting their local school district’s homeschool liaison. Please let
                          us know if you need assistance with your order.
                        </i>

                        {/* <i className="text-sm text-base leading-6 text-primary-500">
                          <a
                            href="https://www.fldoe.org/schools/school-choice/home-edu/district-home-edu-contacts.stml"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Find your Florida Home Education District Liaison →
                          </a>
                        </i> */}
                      </div>

                      <Button disabled={form.invalid} className="px-5">
                        {form.submitting ? (
                          <Spinner size={26} singleColor="#ffffff" />
                        ) : (
                          <>
                            Next <HiArrowRight className="mt-px ml-3" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const StarPage: BlitzPage = () => {
  return (
    <Suspense fallback={<div />}>
      <StarSignup />
    </Suspense>
  )
}

StarPage.getLayout = (page) => (
  <BookingLayout title="Star Testing Registration" subtitle="Star Registration">
    {page}
  </BookingLayout>
)

export default StarPage
